/**
 * Report Utilities
 * 
 * This file contains utility functions for processing and displaying AI report data.
 * 
 * Most business logic functions are imported from @repo/core/client to maintain 
 * a single source of truth and ensure consistency with backend processing.
 * 
 * Local implementations exist only where frontend-specific styling is needed
 * (e.g., highlightCompetitorMentions uses Tailwind classes).
 */

import {
  PLATFORM_DISPLAY_NAMES,
  PLATFORM_LOGOS,
  PLATFORM_ORDER,
  renderMarkdownToHtml,
  detectMarkdown as detectMarkdownCore,
  highlightBrandsInHtml,
  preprocessMixedContent,
  // Brand detection
  isOurBrand as coreIsOurBrand,
  isBrandMentionedInExecution as coreIsBrandMentionedInExecution,
  isCompetitorBrand as coreIsCompetitorBrand,
  getCompetitorNames as coreGetCompetitorNames,
  // Google AI Overview transformation
  transformRawGoogleAiOverview as coreTransformRawGoogleAiOverview,
  // Share of voice calculation
  calculateQueryShareOfVoice as coreCalculateQueryShareOfVoice,
  // Platform analysis
  analyzePlatformConsistency as coreAnalyzePlatformConsistency,
  getPlatformGuideUrl as coreGetPlatformGuideUrl,
} from "@repo/core/client";

// Cache for platform constants fetched from API
let platformDisplayNamesCache: Record<string, string> | null = null;
let platformLogosCache: Record<string, string> | null = null;
let platformOrderCache: Record<string, number> | null = null;

/**
 * Initialize platform constants cache
 * This should be called once when the app loads
 */
export async function initializePlatformConstants(
  displayNames: Record<string, string>,
  logos: Record<string, string>,
  order: Record<string, number>
) {
  platformDisplayNamesCache = displayNames;
  platformLogosCache = logos;
  platformOrderCache = order;
}

/**
 * Get display name for a platform
 * Falls back to imported constants if cache not initialized
 */
export function getPlatformDisplayName(platform: string): string {
  const cache = platformDisplayNamesCache || PLATFORM_DISPLAY_NAMES;
  return cache[platform] || platform.charAt(0).toUpperCase() + platform.slice(1);
}

/**
 * Get logo path for a platform
 * Falls back to imported constants if cache not initialized
 */
export function getPlatformLogo(platform: string): string {
  const cache = platformLogosCache || PLATFORM_LOGOS;
  return cache[platform] || '';
}

/**
 * Get display order for a platform
 * Falls back to imported constants if cache not initialized
 */
export function getPlatformOrder(platform: string): number {
  const cache = platformOrderCache || PLATFORM_ORDER;
  return cache[platform] ?? 999;
}

/**
 * Get competitor names from brand snapshot and property competitors
 * Delegates to core for consistent alias handling
 */
export function getCompetitorNames(brandSnapshot?: any, propertyCompetitors?: any[]): string[] {
  return coreGetCompetitorNames(brandSnapshot, propertyCompetitors);
}

/**
 * Check if a brand name matches our brand (including aliases)
 * Delegates to core for consistent brand matching logic
 * 
 * @param brandName - The brand name to check
 * @param brandSnapshot - Brand snapshot containing the primary brand name
 * @param propertyAliases - Optional array of property aliases to check against
 * @returns True if the brand name matches our brand or any of its aliases
 */
export function isOurBrand(brandName: string, brandSnapshot?: any, propertyAliases?: string[]): boolean {
  return coreIsOurBrand(brandName, brandSnapshot, propertyAliases);
}

/**
 * Returns true if our brand appears in this platform execution's brandMentions array.
 * Single source of truth used by summary, per-query rows, per-platform dots, and badges.
 * Recomputes live; does not trust stored isOurBrand / ourBrandInSummary flags.
 */
export function isBrandMentionedInExecution(
  execution: {
    brandMentions: Array<{ brandName: string; isOurBrand?: boolean; inSummary?: boolean }>;
    error?: string | null;
    completedAt?: string | Date | null;
  },
  brandSnapshot?: any,
  propertyAliases?: string[],
): boolean {
  return coreIsBrandMentionedInExecution(execution, brandSnapshot, propertyAliases);
}

/**
 * Check if a brand name matches a competitor
 * Delegates to core for consistent competitor matching logic
 */
export function isCompetitorBrand(brandName: string, competitorNames: string[]): boolean {
  return coreIsCompetitorBrand(brandName, competitorNames);
}

/**
 * Highlight competitor mentions in text
 * 
 * @param text - The text to highlight
 * @param competitorNames - Array of competitor names and aliases
 * @param brandSnapshot - Brand snapshot containing the primary brand name
 * @param excludeCitations - Whether to skip highlighting text that looks like citations
 * @param propertyAliases - Optional array of property aliases to highlight as our brand
 */
export function highlightCompetitorMentions(
  text: string, 
  competitorNames: string[], 
  brandSnapshot?: any, 
  excludeCitations: boolean = false,
  propertyAliases?: string[]
): string {
  if (!text) {
    return text;
  }
  
  // If excluding citations and this looks like a citation, don't highlight
  if (excludeCitations && (text.includes('http') || text.includes('www.') || text.length < 100)) {
    return text;
  }
  
  let highlightedText = text;
  
  // Collect all our brand names (primary + aliases)
  const ourBrandNames: string[] = [];
  if (brandSnapshot?.data?.name) {
    ourBrandNames.push(brandSnapshot.data.name);
  }
  if (propertyAliases && propertyAliases.length > 0) {
    ourBrandNames.push(...propertyAliases.filter(Boolean));
  }
  
  // Sort by length (longest first) to avoid partial replacement issues
  const sortedBrandNames = [...ourBrandNames].sort((a, b) => b.length - a.length);
  
  // Highlight our brand names (including aliases)
  sortedBrandNames.forEach((brandName) => {
    if (!brandName) return;
    const escapedBrand = brandName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const brandRegex = new RegExp(`\\b(${escapedBrand})\\b`, 'gi');
    highlightedText = highlightedText.replace(brandRegex, '<mark class="bg-blue-100 text-blue-900 px-1 rounded">$1</mark>');
  });
  
  // Then highlight competitors
  competitorNames.forEach((competitor) => {
    const escapedCompetitor = competitor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const competitorRegex = new RegExp(`\\b(${escapedCompetitor})\\b`, 'gi');
    highlightedText = highlightedText.replace(competitorRegex, '<mark class="bg-red-100 text-red-900 px-1 rounded">$1</mark>');
  });
  
  return highlightedText;
}

/**
 * Detect if text contains markdown
 * Uses the improved markdown detector from core
 */
export function detectMarkdown(text: string): boolean {
  return detectMarkdownCore(text);
}

/**
 * Render markdown to HTML using proper remark/rehype pipeline
 * This is now async to support the proper markdown processor
 * 
 * For backward compatibility, you can still use the sync version (renderMarkdownSync)
 * but it uses a simpler regex-based approach.
 */
export async function renderMarkdown(
  text: string,
  options?: {
    highlightBrands?: Array<{ name: string; type: 'our-brand' | 'competitor' }>;
  }
): Promise<string> {
  // Use the proper markdown renderer from core
  let html = await renderMarkdownToHtml(text);
  
  // Apply brand highlighting if requested
  if (options?.highlightBrands && options.highlightBrands.length > 0) {
    html = highlightBrandsInHtml(html, options.highlightBrands);
  }
  
  return html;
}

/**
 * Synchronous markdown renderer (backward compatibility)
 * Uses simple regex-based approach - less accurate but doesn't require async
 * 
 * @deprecated Use renderMarkdown() for better markdown support
 */
export function renderMarkdownSync(text: string): string {
  let result = text;
  
  // Bold
  result = result.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  
  // Italic
  result = result.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  
  // Links
  result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">$1</a>');
  
  // Headers (simple - just make them bold and larger)
  result = result.replace(/^######\s+(.+)$/gm, '<h6 class="text-base font-semibold mt-4 mb-2">$1</h6>');
  result = result.replace(/^#####\s+(.+)$/gm, '<h5 class="text-lg font-semibold mt-4 mb-2">$1</h5>');
  result = result.replace(/^####\s+(.+)$/gm, '<h4 class="text-xl font-semibold mt-4 mb-2">$1</h4>');
  result = result.replace(/^###\s+(.+)$/gm, '<h3 class="text-2xl font-semibold mt-6 mb-3">$1</h3>');
  result = result.replace(/^##\s+(.+)$/gm, '<h2 class="text-3xl font-bold mt-8 mb-4">$1</h2>');
  result = result.replace(/^#\s+(.+)$/gm, '<h1 class="text-4xl font-bold mt-8 mb-4">$1</h1>');
  
  // Line breaks (convert double newlines to paragraphs, single to br)
  result = result.replace(/\n\n/g, '</p><p class="mb-4">');
  result = result.replace(/\n/g, '<br />');
  result = '<p class="mb-4">' + result + '</p>';
  
  return result;
}

/**
 * Transform raw Google AI Overview data from DataForSEO API to frontend format
 * Delegates to core for consistent processing
 */
export const transformRawGoogleAiOverview = coreTransformRawGoogleAiOverview;

/**
 * Calculate share of voice for a single query
 * Delegates to core for consistent calculation
 */
export function calculateQueryShareOfVoice(
  discoveryExecution: any, 
  competitorNames: string[], 
  brandSnapshot?: any,
  propertyAliases?: string[]
): {
  shareOfVoice: number;
  ourPlatformMentions: number;
  competitorPlatformMentions: number;
  totalPlatforms: number;
} {
  return coreCalculateQueryShareOfVoice(discoveryExecution, competitorNames, brandSnapshot, propertyAliases);
}

/**
 * Analyze platform consistency
 * Delegates to core for consistent analysis logic
 */
export const analyzePlatformConsistency = coreAnalyzePlatformConsistency;

/**
 * Get platform guide URL
 * Delegates to core for consistent URL mapping
 */
export const getPlatformGuideUrl = coreGetPlatformGuideUrl;

/**
 * Process discovery response with backward compatibility
 * Handles both old API-based format and new DataForSEO format
 * 
 * @param response - The raw discovery response (API or DataForSEO format)
 * @param options - Processing options including competitor names, brand snapshot, and property aliases
 */
export async function processDiscoveryResponse(
  response: any,
  options?: {
    competitorNames?: string[];
    brandSnapshot?: any;
    /** Property aliases for brand detection (e.g., ["Galleri", "Pathfinder"] for a property named "Grail") */
    propertyAliases?: string[];
  }
): Promise<{
  htmlContent: string;
  isMarkdown: boolean;
  brandMentions: Array<{ name: string; rank: number; context: string; type?: 'our-brand' | 'competitor' }>;
  citations: Array<{ url: string; title?: string; description?: string; attributed_to?: string }>;
}> {
  // Extract answer summary (backward compatible)
  const answerSummary = response.answer_summary || response.markdown || response.summary || '';
  
  // Detect if markdown (check both new flag and content)
  const isMarkdown = response.isMarkdown !== undefined 
    ? response.isMarkdown 
    : detectMarkdown(answerSummary);
  
  // Extract brand mentions (backward compatible)
  let brandMentions = response.brands_mentioned || response.brandMentions || [];
  
  // Add type classification and filter to only relevant brands
  if (options?.competitorNames || options?.brandSnapshot) {
    brandMentions = brandMentions
      .map((mention: any) => {
        const mentionName = mention.name || mention.brandName;
        let type: 'our-brand' | 'competitor' | undefined;
        
        if (options.brandSnapshot && isOurBrand(mentionName, options.brandSnapshot, options?.propertyAliases)) {
          type = 'our-brand';
        } else if (options.competitorNames && isCompetitorBrand(mentionName, options.competitorNames)) {
          type = 'competitor';
        }
        
        return {
          name: mentionName,
          rank: mention.rank || 0,
          context: mention.context || '',
          type
        };
      })
      // FILTER: Only keep brands that are identified as our brand or competitors
      // This removes markdown artifacts like "pull up a full list", "8-15 of the best", etc.
      .filter((mention: any) => mention.type !== undefined);
  }
  
  // Extract citations (backward compatible)
  const citations = response.citations || response.sources || [];
  
  // Render HTML
  let htmlContent: string;
  
  if (isMarkdown) {
    // Use proper markdown renderer with brand highlighting
    // Include property aliases in the highlighting list
    const brandsForHighlighting: Array<{ name: string; type: 'our-brand' | 'competitor' }> = brandMentions
      .filter((m: any) => m.type)
      .map((m: any) => ({ name: m.name, type: m.type }));
    
    // Add property aliases as our-brand entries for highlighting
    if (options?.propertyAliases && options.propertyAliases.length > 0) {
      options.propertyAliases.forEach(alias => {
        if (alias && !brandsForHighlighting.some(b => b.name.toLowerCase() === alias.toLowerCase())) {
          brandsForHighlighting.push({ name: alias, type: 'our-brand' });
        }
      });
    }
    
    htmlContent = await renderMarkdown(answerSummary, {
      highlightBrands: brandsForHighlighting.length > 0 ? brandsForHighlighting : undefined
    });
  } else {
    // Plain text - just escape HTML and preserve line breaks
    htmlContent = answerSummary
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br />');
      
    // Apply brand highlighting even to plain text
    if (options?.competitorNames || options?.brandSnapshot) {
      htmlContent = highlightCompetitorMentions(
        htmlContent, 
        options.competitorNames || [], 
        options.brandSnapshot,
        false,
        options?.propertyAliases
      );
    }
  }

  return {
    htmlContent,
    isMarkdown,
    brandMentions,
    citations
  };
}

